import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight, FiLayers, FiAward, FiTarget } from 'react-icons/fi';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { BRAND, Page, PageHeader, Card, Tag, Segmented, EmptyState, Avatar, CAST, deckMeta, fadeUp, stagger } from '../common/ui';

const HERO = { img: '/illustrations/lesson1/scenes/library.jpg', pos: 'center 28%', height: 640, opacity: 0.6 };

function LessonRows({ items, linkPrefix }) {
  if (!items.length) {
    return <EmptyState icon="📘" title="No lessons yet" text="This course has no lessons published. They will appear here as soon as they are seeded." />;
  }
  return (
    <motion.div variants={stagger} initial="hidden" animate="visible" style={{ display: 'grid', gap: 12 }}>
      {items.map((item, i) => {
        const meta = deckMeta(item);
        const host = CAST[meta.host];
        const num = String(i + 1).padStart(2, '0');
        const interactive = meta.slides > 0;
        const card = (
          <Card hover style={{ padding: 0, overflow: 'hidden', display: 'flex', alignItems: 'stretch', cursor: linkPrefix ? 'pointer' : 'default' }} className="core-lesson-row">
            {/* thumbnail */}
            <div className="core-lesson-thumb" style={{ position: 'relative', width: 200, flexShrink: 0, background: BRAND.navy800, overflow: 'hidden' }}>
              {meta.img
                ? <img src={meta.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                : <img src={BRAND.bg.classroom} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: 0.45 }} />}
              <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(7,20,45,0.1), rgba(7,20,45,0.55))' }} />
              <span aria-hidden style={{ position: 'absolute', left: 12, bottom: 2, fontSize: '2.6rem', fontWeight: 900, lineHeight: 1, color: BRAND.goldLight, textShadow: '0 4px 18px rgba(0,0,0,0.6)' }}>{num}</span>
              {host && <Avatar cast={meta.host} size={30} style={{ position: 'absolute', right: 10, top: 10 }} />}
            </div>

            {/* body */}
            <div style={{ flex: 1, minWidth: 0, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontWeight: 800, letterSpacing: '0.2em', fontSize: '0.68rem', textTransform: 'uppercase', color: BRAND.goldLight }}>Lesson {i + 1}</span>
                {interactive ? <Tag tone={meta.isCore ? 'solid' : 'gold'}>{meta.isCore ? 'CORE deck' : 'Interactive'}</Tag> : <Tag tone="muted">HTML</Tag>}
                {meta.slides > 0 && <Tag tone="muted"><FiLayers size={11} /> {meta.slides} slides</Tag>}
                {meta.missions > 0 && <Tag tone="muted"><FiTarget size={11} /> {meta.missions} missions</Tag>}
                {meta.badges > 0 && <Tag tone="muted"><FiAward size={11} /> {meta.badges} badges</Tag>}
              </div>
              <h2 style={{ fontSize: '1.12rem', fontWeight: 900, color: BRAND.ivory, margin: 0, lineHeight: 1.25 }}>{item.title || `Lesson ${i + 1}`}</h2>
              {item.description && (
                <p style={{ fontSize: '0.88rem', color: BRAND.muted, lineHeight: 1.45, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.description}</p>
              )}
              <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', paddingTop: 4 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, fontSize: '0.78rem', color: BRAND.muted, fontWeight: 700 }}>
                  {host && <span>with {host.name}</span>}
                </span>
                {linkPrefix && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: BRAND.goldLight, fontWeight: 800, fontSize: '0.86rem' }}>
                    Open lesson <FiArrowRight size={14} />
                  </span>
                )}
              </div>
            </div>
          </Card>
        );

        if (linkPrefix) {
          return (
            <motion.div key={item._id || i} variants={fadeUp}>
              <Link to={`${linkPrefix}/${item._id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                {card}
              </Link>
            </motion.div>
          );
        }
        return <motion.div key={item._id || i} variants={fadeUp}>{card}</motion.div>;
      })}
    </motion.div>
  );
}

export default function AdminCourseContent() {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const { data } = await api.get('/courses');
        const raw = data.courses || data || [];
        setCourses(Array.isArray(raw) ? raw : []);
        // Keep the spinner up until the first course's lessons are fetched too,
        // otherwise the page flashes "No lessons yet" while the second request runs.
        if (raw.length > 0) { setLessonsLoading(true); setSelectedCourse(raw[0]); }
      } catch {
        setCourses([]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (!selectedCourse) return;
    let cancelled = false;
    async function loadContent() {
      setLessonsLoading(true);
      try {
        const lessonsRes = await api
          .get(`/lessons/course/${selectedCourse._id}`)
          .catch(() => ({ data: { lessons: [] } }));
        if (!cancelled) setLessons(lessonsRes.data?.lessons || []);
      } catch {
        if (!cancelled) setLessons([]);
      } finally {
        if (!cancelled) setLessonsLoading(false);
      }
    }
    loadContent();
    return () => { cancelled = true; };
  }, [selectedCourse]);

  if (loading || (lessonsLoading && lessons.length === 0)) {
    return (
      <Page hero={HERO}>
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <LoadingSpinner message="Loading the courses…" />
        </Card>
      </Page>
    );
  }

  return (
    <Page hero={HERO}>
      <style>{`
        @media (max-width: 640px) {
          .core-lesson-row { flex-direction: column !important; }
          .core-lesson-thumb { width: 100% !important; aspect-ratio: 16 / 7; }
        }
      `}</style>

      <PageHeader
        kicker="Administration"
        title="Course content"
        subtitle={selectedCourse
          ? `${selectedCourse.title} — ${lessons.length} ${lessons.length === 1 ? 'lesson' : 'lessons'}`
          : 'Pick a course to browse its lessons.'}
        right={courses.length > 0 && (
          <Segmented
            value={selectedCourse?._id || ''}
            onChange={(id) => {
              const c = courses.find((x) => x._id === id);
              if (c) setSelectedCourse(c);
            }}
            options={courses.map((c) => ({
              value: c._id,
              label: `${c.icon ? `${c.icon} ` : ''}${c.title}`,
            }))}
          />
        )}
      />

      {courses.length === 0 && <EmptyState icon="📚" title="No courses" text="There are no active courses to show right now." />}

      {selectedCourse && (
        <motion.div
          key={selectedCourse?._id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <LessonRows items={lessons} linkPrefix="/admin/lezioni" />
        </motion.div>
      )}
    </Page>
  );
}
